<template>
    <v-dialog v-model="dialog" persistent max-width="600px">
        <template v-slot:activator="{ props }">
            <v-btn
                disabled
                class="btn"
                variant="text"
                size="small"
                v-bind="props"
            >
                <v-icon size="small">mdi-plus</v-icon>
                &nbsp;
                {{ dialogText }}
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                <span class="text-body-large text--typography1">
                    New {{ recordType }} of {{ calc.name }} revision
                    {{ getLatestRevision() }}
                </span>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-row wrap>
                        <span v-show="recordType === 'review'">
                            <v-col cols="12">
                                <span class="headline">Comments</span>
                            </v-col>
                            <v-col cols="12">
                                <span class="subheading">
                                    Add specific comments about the calculation
                                    below. By breaking your review up into
                                    specific items it will allow for more
                                    fine-grained discussion and resolution. The
                                    comments will be added when submit is
                                    clicked.
                                </span>
                            </v-col>
                            <v-col
                                cols="12"
                                v-for="(comment, index) in commentBoxes"
                                :key="index"
                            >
                                <v-textarea
                                    rows="1"
                                    color="secondary"
                                    auto-grow
                                    :append-inner-icon="
                                        allowedSendIcon(index)
                                            ? 'mdi-send'
                                            : undefined
                                    "
                                    @click:append-inner="addBox(index)"
                                    v-model="commentBoxes[index]"
                                ></v-textarea>
                            </v-col>
                            <v-col cols="12">
                                <span class="headline">Summary</span>
                            </v-col>
                            <v-col cols="12">
                                <span class="subheading">
                                    For your overall status and summary comment
                                    think about what you would say to colleague
                                    if they asked for your opinion on the
                                    calculation overall.
                                </span>
                            </v-col>
                        </span>
                        <v-col cols="12">
                            <v-form
                                ref="form"
                                lazy-validation
                                @submit.prevent="submitClicked"
                            >
                                <v-autocomplete
                                    color="secondary"
                                    v-show="recordType === 'review'"
                                    :items="possibleStatus"
                                    v-model="status"
                                    item-title="name"
                                    item-value="table"
                                    label="Status"
                                    prepend-icon="mdi-checkbox-marked"
                                    variant="outlined"
                                ></v-autocomplete>
                                <Projects
                                    v-show="projects"
                                    :initialProject="reference_string"
                                    @changeProject="reference_string = $event"
                                />
                                <SkillsNetwork
                                    v-show="skillsNetwork"
                                    :initialNetwork="reference_string"
                                    @changeNetwork="reference_string = $event"
                                />

                                <v-textarea
                                    rows="1"
                                    color="secondary"
                                    auto-grow
                                    prepend-icon="mdi-comment"
                                    label="Summary comment"
                                    v-model="baseComment"
                                ></v-textarea>
                            </v-form>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
            <v-card-actions class="justify-space-around">
                <v-btn @click="closed()" color="error"> CANCEL </v-btn>
                <v-btn
                    @click="newRecord()"
                    id="submit-btn"
                    color="secondary"
                    type="submit"
                    disabled
                    :loading="loadingDialog"
                >
                    SUBMIT
                </v-btn>
            </v-card-actions>
        </v-card>
        <v-dialog v-model="loadingDialog" persistent width="300">
            <v-card color="typography3" class="text-white">
                <v-card-text class="text-typography1">
                    Please stand by
                    <v-progress-linear
                        indeterminate
                        class="mb-0"
                        color="secondary"
                    ></v-progress-linear>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>
<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { v4 as uuidv4 } from 'uuid'
import Projects from './Projects.vue'
import SkillsNetwork from './SkillsNetwork.vue'

const props = defineProps({
    calc: { type: Object, required: true },
    skillsNetwork: { type: Boolean },
    projects: { type: Boolean },
})

const store = useStore()
const router = useRouter()

const dialog = ref(false)
const possibleStatus = [
    { table: 'recommended', name: 'Recommended' },
    { table: 'recommended_caveats', name: 'Recommended With Conditions' },
    { table: 'not_recommended', name: 'Not Recommended' },
]
const status = ref(null)
const baseComment = ref('')
const loadingDialog = ref(false)
const reference_string = ref('')
const commentBoxes = ref([''])

const dialogText = computed(() => {
    if (props.skillsNetwork) return 'Create New Skills Network Recommendation'
    if (props.projects) return 'Create New Project Approval'
    return 'Create New Review'
})

const recordType = computed(() => {
    if (props.skillsNetwork || props.projects) return 'approval'
    return 'review'
})

const tableType = computed(() => {
    if (props.projects) return 'approval_project'
    if (props.skillsNetwork) return 'approval_skills_network'
    return 'review'
})

const recordTable = computed(() => reference_string.value || recordType.value)

const summaryStatus = computed(() => {
    if (recordType.value === 'review') return status.value
    return 'approved'
})

function allowedSendIcon(index) {
    return commentBoxes.value.length - 1 === index
}

function addBox(index) {
    if (commentBoxes.value[index] !== '') commentBoxes.value.push('')
}

function closed() {
    commentBoxes.value = ['']
    baseComment.value = ''
    status.value = null
    dialog.value = false
    reference_string.value = ''
}

function getLatestRevision() {
    const revisions = props.calc.revisions
    let highest = revisions[0]
    revisions.forEach((revision) => {
        if (
            parseFloat(revision.split('-')[0]) >
            parseFloat(highest.split('-')[0])
        )
            highest = revision
    })
    return highest
}

async function createThreads(reference, refUrl) {
    for (let i = 0; i < commentBoxes.value.length; i++) {
        if (commentBoxes.value[i] !== '') {
            await store.dispatch('createComment', {
                parent: reference,
                content: commentBoxes.value[i],
                refUrl: refUrl,
                referenceId: reference,
                level: 1,
            })
        }
    }
}

async function newRecord() {
    loadingDialog.value = true
    const flow = await store.dispatch(
        'getFlow',
        'arupcompute_' + tableType.value
    )
    const type = await store.dispatch('getFlowType', {
        flowId: flow.id,
        name: summaryStatus.value,
    })
    const reference = uuidv4()
    const calcIdentifier =
        props.calc.libraryVersion.libraryId +
        '_' +
        props.calc.persistentCalcID +
        '_' +
        getLatestRevision()
    const refUrl =
        process.env.VUE_APP_API_URL +
        calcIdentifier +
        '/' +
        recordType.value +
        's/' +
        reference
    const comment = await store.dispatch('createComment', {
        parent: reference,
        content: baseComment.value,
        refUrl: refUrl,
        referenceId: reference,
        level: 0,
    })
    const newRec = await store.dispatch('createRecord', {
        typeId: type.id,
        comment: comment.id,
        reference: reference,
        table: recordTable.value,
        refUrl: refUrl,
    })
    await createThreads(reference, refUrl)
    await store.dispatch('getRecord', newRec.reference_id)
    loadingDialog.value = false
    router.push({
        name: recordType.value + 's',
        params: { calcId: props.calc.calcId, recordId: reference },
    })
}
</script>
<style scoped>
.btn {
    text-transform: none;
}
</style>
